import { useState } from "react";
import achievements from "../data/achievements.json";
import { useScrollReveal } from "../hooks/useScrollReveal";

const Achievements = () => {
  const [showAll, setShowAll] = useState(false);
  const [selectedInd, setSelectedInd] = useState<number | null>(null);
  const visibleAchievements = showAll ? achievements : achievements.slice(0, 6);
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal({ threshold: 0.3 });
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal({ threshold: 0.05 });

  const toggleExpand = (index: number) => {
    setSelectedInd(selectedInd === index ? null : index);
  };

  return (
    <section id="achievements" className="section-padding bg-[var(--section-alt-bg)] relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-orange-400/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />

      <div className="max-w-7xl mx-auto relative">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-12 md:mb-16 transition-all duration-700 ${
            headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 text-xs font-semibold uppercase tracking-wider mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
            Excellence
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--text-primary)] mb-4 gowun-batang">
            Our Champions
          </h2>
          <p className="text-[var(--text-secondary)] max-w-2xl mx-auto text-base md:text-lg">
            Outstanding achievements of our athletes under the guidance of{" "}
            <span className="font-semibold text-orange-500">
              Coach Anil Pande
            </span>
          </p>
        </div>

        {/* Achievements Grid */}
        <div
          ref={gridRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
        >
          {visibleAchievements.map((ach, index) => {
            const isExpanded = selectedInd === index;
            const displayDetails = isExpanded ? ach.details : ach.details.slice(0, 3);
            const discipline = ach.name.match(/\(([^)]+)\)/)?.[1] || "";

            return (
              <div
                key={index}
                className={`card overflow-hidden transition-all duration-500 ${
                  isExpanded ? "ring-2 ring-orange-400/50 shadow-lg" : ""
                } ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
                style={{ transitionDelay: `${index * 80}ms` }}
              >
                {/* Card Top Accent */}
                <div className="h-1 w-full bg-gradient-to-r from-orange-400 to-orange-600" />

                <div className="p-4 sm:p-5 md:p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <div className="flex items-center gap-2.5 sm:gap-3">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                          <path fillRule="evenodd" d="M5.166 2.621v.858c-1.035.148-2.059.33-3.071.543a.75.75 0 00-.584.859 6.753 6.753 0 006.138 5.6 6.73 6.73 0 002.743 1.346A6.707 6.707 0 019.279 15H8.54c-1.036 0-1.875.84-1.875 1.875V19.5h-.75a.75.75 0 000 1.5h12.17a.75.75 0 000-1.5h-.75v-2.625c0-1.036-.84-1.875-1.875-1.875h-.739a6.707 6.707 0 01-1.112-3.173 6.73 6.73 0 002.743-1.347 6.753 6.753 0 006.139-5.6.75.75 0 00-.585-.858 47.077 47.077 0 00-3.07-.543V2.62a.75.75 0 00-.658-.744 49.22 49.22 0 00-6.093-.377c-2.063 0-4.096.128-6.093.377a.75.75 0 00-.657.744z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-bold text-[var(--text-primary)] gowun-batang text-base leading-tight">
                          {ach.name.split(" (")[0].replace(":", "")}
                        </h4>
                      </div>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 text-xs font-semibold whitespace-nowrap">
                      {discipline}
                    </span>
                  </div>

                  {/* Details List */}
                  <div className="space-y-2">
                    {displayDetails.map((detail, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-2 sm:gap-2.5 p-2.5 sm:p-3 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-secondary)]"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-2 flex-shrink-0" />
                        <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                          {detail}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Expand/Collapse */}
                  {ach.details.length > 3 && (
                    <button
                      onClick={() => toggleExpand(index)}
                      className="mt-3 text-xs font-semibold text-orange-500 hover:text-orange-600 transition-colors duration-200 flex items-center gap-1"
                    >
                      {isExpanded ? (
                        <>Show less</>
                      ) : (
                        <>
                          +{ach.details.length - 3} more achievements
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                          </svg>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Show More / Less */}
        {achievements.length > 6 && (
          <div className={`mt-10 flex justify-center transition-all duration-500 delay-500 ${
            gridVisible ? "opacity-100" : "opacity-0"
          }`}>
            <button
              onClick={() => {
                setShowAll((prev) => !prev);
                if (showAll) {
                  const el = document.getElementById("achievements");
                  if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: "smooth" });
                }
              }}
              className="btn-primary group"
            >
              {showAll ? "Show Less" : `View All ${achievements.length} Champions`}
              {!showAll && (
                <svg className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5L12 21m0 0l-7.5-7.5M12 21V3" />
                </svg>
              )}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Achievements;
